import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { useAppSelector } from "app/hooks";
import { selectCurrentUser } from "features/auth/auth-slice";

import {
  REPORT_REGISTRY,
  resolveReportIdFromPathname
} from "../report-registry.config";
import useReportPermissions from "./use-report-permissions.hook";

export type ReportFilterLock = {
  /** The filter key to pin to the current user, when this report is scoped to their own records. */
  lockedStaffFilter?: string;
  /** The current user's id, as the filter option value (string, matching the picker's options). */
  lockedStaffId?: string;
  /** The filter key whose options are limited, when this report is scoped to specific clinics. */
  scopedClinicFilter?: string;
  /** Clinic ids the user may filter by, as option values. */
  allowedClinicIds?: string[];
};

const NO_LOCK: ReportFilterLock = {};

/**
 * Which report filters are locked for the current user on the report they're viewing.
 *
 * `self_only` pins the report's staff/provider filter to the user; `clinic_scope: 'selected'` limits
 * its clinic filter to the chosen clinics. The report is identified from the URL by
 * `resolveReportIdFromPathname`, matching `useCanDownloadCurrentReport` — outside the reports
 * module there is never a lock, even where another feature's route reuses a report slug
 * (`/billing/payments`, QuickBooks `refunds`/`taxes`).
 *
 * Returns nothing unless that report has an explicit entry asking for it — so a user who is
 * unconfigured, in simple mode, or with the flag off is untouched by construction.
 *
 * This locks the UI only; it is a curation guardrail, not a security boundary (the request params
 * remain client-supplied).
 */
export default function useReportFilterLock(): ReportFilterLock {
  const { pathname } = useLocation();
  const currentUser = useAppSelector(selectCurrentUser);
  const permissions = useReportPermissions();

  return useMemo(() => {
    const reportId = resolveReportIdFromPathname(pathname);
    if (!reportId) return NO_LOCK;

    const access = permissions[reportId];
    const entry = REPORT_REGISTRY[reportId];
    if (!access) return NO_LOCK;

    const lock: ReportFilterLock = {};

    if (access.selfOnly && entry.staffFilter && currentUser?.id) {
      lock.lockedStaffFilter = entry.staffFilter;
      lock.lockedStaffId = String(currentUser.id);
    }

    // A "selected" scope with no clinics would offer an empty picker while the request goes out
    // unscoped — treat it as "all clinics". New saves normalize this away; the guard covers blobs
    // written before they did.
    if (
      access.clinicScope === "selected" &&
      entry.clinicFilter &&
      (access.selectedClinicIds ?? []).length > 0
    ) {
      lock.scopedClinicFilter = entry.clinicFilter;
      lock.allowedClinicIds = access.selectedClinicIds.map(String);
    }

    return lock;
  }, [pathname, permissions, currentUser?.id]);
}
